import Coloris from '@melloware/coloris';

let colorisInitialized = false;

const colorisPicker = () => document.getElementById('clr-picker');

const configureColoris = parent => {
  if (!colorisInitialized) {
    Coloris.init();
    colorisInitialized = true;
  }

  Coloris({
    el: '[data-coloris]',
    parent: parent || document.body,
    theme: 'polaroid',
    themeMode: 'light',
    alpha: false,
    format: 'hex',
    wrap: true,
    closeButton: true,
    swatches: ['#ffffff', '#f4f4f5', '#e4e4e7', '#3f3f46', '#71717a', '#4f46e5', '#0f766e', '#18181b'],
  });

  colorisPicker()?.removeAttribute('inert');
};

const parkColorisPicker = () => {
  Coloris.close();
  const picker = colorisPicker();
  if (picker && picker.parentElement !== document.body) {
    document.body.appendChild(picker);
  }
};

const fontStacksOf = control => JSON.parse(control?.dataset.fontStacks || '{}');

const fontSelectOf = control =>
  control?.querySelector('select[data-part="select"]') || control?.querySelector('select');

const fontIdOf = control => {
  const select = fontSelectOf(control);
  if (select?.value) return select.value;

  const selectedOption = select?.querySelector('option[selected]:not([value=""])');
  if (selectedOption?.value) return selectedOption.value;

  const selected = control?.querySelector(
    '[data-part="option"][data-selected="true"], [data-part="option"][aria-selected="true"]',
  );
  return selected?.dataset.value;
};

const isFontControl = input => input?.hasAttribute?.('data-token-font') || !!input?.closest?.('[data-token-font]');

const fontControlOf = input =>
  input?.hasAttribute?.('data-token-font') ? input : input?.closest?.('[data-token-font]');

const valueOf = input => {
  const fontControl = fontControlOf(input);

  if (fontControl) {
    const id = fontIdOf(fontControl);
    const stacks = fontStacksOf(fontControl);
    return (id && stacks[id]) || undefined;
  }

  return `${input.value}${input.dataset.tokenUnit || ''}`;
};

const tokenVars = input => input.dataset.tokenVars.split(' ').filter(Boolean);

const show = input => {
  const display = input.closest('div')?.querySelector('[data-token-display]');
  if (display && !input.dataset.tokenFont) display.textContent = valueOf(input);
};

const onPrimaryText = hex => {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!match) return '#ffffff';

  const n = parseInt(match[1], 16);
  const toLinear = c => {
    const channel = c / 255;
    return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  };
  const luminance = 0.2126 * toLinear((n >> 16) & 255) + 0.7152 * toLinear((n >> 8) & 255) + 0.0722 * toLinear(n & 255);

  return luminance > 0.5 ? '#18181b' : '#ffffff';
};

const applyInput = (scope, input) => {
  const value = valueOf(input);
  if (!value) return;

  tokenVars(input).forEach(name => scope.style.setProperty(name, value));
  if (tokenVars(input).includes('--primary')) {
    scope.style.setProperty('--foreground-primary', onPrimaryText(value));
  }
  if (isFontControl(input)) scope.style.fontFamily = value;
};

const applyFontId = (root, id) => {
  const scope = root.querySelector('[data-theme-scope]');
  const fontControl = root.querySelector('[data-token-font]');
  if (!scope || !fontControl || !id) return;

  const stack = fontStacksOf(fontControl)[id] || id;
  scope.style.setProperty('--font-sans', stack);
  scope.style.fontFamily = stack;
};

const applyTokens = root => {
  const scope = root.querySelector('[data-theme-scope]');
  if (!scope) return;

  Array.from(root.querySelectorAll('[data-token-vars]')).forEach(input => {
    show(input);
    applyInput(scope, input);
  });
};

const controlFromEvent = event => {
  const target = event.target;
  return target.closest?.('[data-token-vars]') || (target.dataset?.tokenVars ? target : null);
};

export default {
  ThemeTokens: {
    mounted() {
      configureColoris(this.el.closest('[data-part="dialog-wrapper"]') || document.body);

      this.onTokenEvent = event => {
        const scope = this.el.querySelector('[data-theme-scope]');
        const control = controlFromEvent(event);
        if (!scope || !control) return;

        show(control);
        applyInput(scope, control);
      };

      this.onFontOption = event => {
        const option = event.target.closest?.('[data-part="option"]');
        if (!option) return;

        const listbox = option.closest('[data-part="listbox"]');
        if (!listbox || !String(listbox.id || '').includes('font_family')) return;

        applyFontId(this.el, option.dataset.value);
      };

      this.onFontSelect = event => applyFontId(this.el, event.target.value);

      this.el.addEventListener('input', this.onTokenEvent);
      this.el.addEventListener('change', this.onTokenEvent);
      this.el.addEventListener('coloris:pick', this.onTokenEvent);
      document.addEventListener('pointerdown', this.onFontOption, true);

      this.fontSelect = fontSelectOf(this.el.querySelector('[data-token-font]'));
      this.fontSelect?.addEventListener('change', this.onFontSelect);

      applyTokens(this.el);
    },

    updated() {
      applyTokens(this.el);
    },

    destroyed() {
      parkColorisPicker();

      this.el.removeEventListener('input', this.onTokenEvent);
      this.el.removeEventListener('change', this.onTokenEvent);
      this.el.removeEventListener('coloris:pick', this.onTokenEvent);
      document.removeEventListener('pointerdown', this.onFontOption, true);
      this.fontSelect?.removeEventListener('change', this.onFontSelect);
    },
  },
};
